import React, { useState, useEffect, useMemo, useCallback, createContext } from 'react';
import PropTypes from 'prop-types';
import BZAPIService from '../services/BZAPIService';
import { initPlanOddStage } from '../helpers/planConfig';
import { oddStageOf } from '../helpers/oddStage';

const TenantSettingsContext = createContext({
  bsaEnabled: true,
  planOddStage: 'prod',
  planEnabled: true,
  preventEnabled: true,
  predictEnabled: false,
  bugCoverageEnabled: true,
  setBugCoverageEnabled: () => {},
  setProductEnabled: () => {},
  isLoaded: false
});

const TenantSettingsProvider = ({ children }) => {
  const [bsaEnabled, setBsaEnabled] = useState(true);
  const [planOddStage, setPlanOddStage] = useState('prod');
  const [planEnabled, setPlanEnabled] = useState(true);
  const [preventEnabled, setPreventEnabled] = useState(true);
  const [predictEnabled, setPredictEnabled] = useState(false);
  const [bugCoverageEnabled, setBugCoverageEnabled] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);

  // Super Admin's product switches call this once the write lands, so a product
  // turned on or off shows or hides at once, without a reload.
  const setProductEnabled = useCallback((product, enabled) => {
    const setter = { plan: setPlanEnabled, prevent: setPreventEnabled, predict: setPredictEnabled }[product];
    if (setter) setter(enabled === true);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      // One request for every row, not one per setting (STRY0038868). Each
      // parallel request needed its own backend instance, so after an idle
      // break the app paid six cold starts here — and ProductGuard renders
      // nothing until this resolves.
      let rowsById = new Map();
      try {
        const rows = await BZAPIService.getTenantSettings();
        rowsById = new Map((Array.isArray(rows) ? rows : []).map(row => [row?.id, row]));
      } catch (error) {
        console.warn('Failed to fetch tenant settings, defaulting every setting but Predict to enabled:', error);
      }

      if (cancelled) return;

      // A missing row (or a failed request) leaves the setting on, exactly as
      // a 404 on the old per-setting request did — including the Super Admin
      // "Bug coverage" switch (STRY0038808). Predict is the exception: it
      // starts off for every customer until a Super Admin turns it on
      // (STRY0038309), so only an explicit true opens it.
      const valueOf = settingId => rowsById.get(settingId)?.value?.value;
      const isOn = settingId => valueOf(settingId) !== false;

      setBsaEnabled(isOn('bsaEnabled'));
      // bsaProd holds Plan's ODD stage (STRY0039057); missing means prod.
      const stage = oddStageOf(valueOf('bsaProd'));
      setPlanOddStage(stage);
      initPlanOddStage(stage);
      setPlanEnabled(isOn('planEnabled'));
      setPreventEnabled(isOn('preventEnabled'));
      setPredictEnabled(valueOf('predictEnabled') === true);
      setBugCoverageEnabled(isOn('bugCoverageEnabled'));

      setIsLoaded(true);
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(
    () => ({
      bsaEnabled,
      planOddStage,
      planEnabled,
      preventEnabled,
      predictEnabled,
      bugCoverageEnabled,
      setBugCoverageEnabled,
      setProductEnabled,
      isLoaded
    }),
    [
      bsaEnabled,
      planOddStage,
      planEnabled,
      preventEnabled,
      predictEnabled,
      bugCoverageEnabled,
      setBugCoverageEnabled,
      setProductEnabled,
      isLoaded
    ]
  );

  return <TenantSettingsContext.Provider value={value}>{children}</TenantSettingsContext.Provider>;
};

TenantSettingsProvider.propTypes = { children: PropTypes.node };

export { TenantSettingsContext, TenantSettingsProvider };
